import { Button, Icon, Text } from "@ttc3k/trekker";
import { Xmark } from "iconoir-react";
import { useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { es, fr } from "date-fns/locale";
import { EnumPromotionAvailabilityType } from "gql/generated";
import { Flex, styled } from "styled-system/jsx";
import { PromotionSectionHeader } from "./PromotionSectionHeader";
import { PromotionDateField } from "./PromotionDateField";
import type { PromotionFormValues } from "../promotionForm.schema";

const MODES: { type: EnumPromotionAvailabilityType; key: string }[] = [
  { type: EnumPromotionAvailabilityType.SpecificDates, key: "SPECIFIC_DATES" },
  { type: EnumPromotionAvailabilityType.DateRange, key: "DATE_RANGE" },
  { type: EnumPromotionAvailabilityType.Ongoing, key: "ONGOING" },
];

type PromotionDatesSectionProps = { disabled?: boolean };

export const PromotionDatesSection = ({
  disabled,
}: PromotionDatesSectionProps) => {
  const { t, i18n } = useTranslation("events", {
    keyPrefix: "APP.PROMOTION_MODAL.DATES",
  });
  const locale =
    i18n.language === "fr" ? fr : i18n.language === "es" ? es : undefined;
  const { control, setValue, formState, clearErrors, trigger } =
    useFormContext<PromotionFormValues>();
  const availabilityType = useWatch({ control, name: "availabilityType" });
  const dates = useWatch({ control, name: "dates" });
  const startDate = useWatch({ control, name: "startDate" });
  const endDate = useWatch({ control, name: "endDate" });

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const handleOpenChange = useCallback(
    (index: number, open: boolean) => setOpenIndex(open ? index : null),
    [],
  );

  // The range rules span both fields, so re-validate them as a pair
  const setRangeDate = (field: "startDate" | "endDate", value: string) => {
    setValue(field, value, { shouldDirty: true });
    clearErrors(["startDate", "endDate"]);
    void trigger(["startDate", "endDate"]);
  };

  const setDates = (next: string[]) => {
    setValue("dates", next, { shouldDirty: true, shouldValidate: true });
    clearErrors("dates");
  };

  const datesError = formState.errors.dates?.message as string | undefined;
  const rangeError =
    (formState.errors.startDate?.message as string | undefined) ??
    (formState.errors.endDate?.message as string | undefined);

  return (
    <Flex flexDir="column" gap="300">
      <PromotionSectionHeader
        step={2}
        title={t("TITLE")}
        description={t("DESCRIPTION")}
      />
      <Flex flexDir="column" gap="200">
        {/* Mode toggle */}
        <Flex gap="100">
          {MODES.map(({ type, key }) => {
            const selected = availabilityType === type;
            return (
              <styled.button
                key={type}
                type="button"
                flex="1"
                h="58px"
                px="200"
                borderRadius="100"
                cursor="pointer"
                bg={selected ? "white" : "ink.100"}
                border={selected ? "2px solid" : "1px solid"}
                borderColor={selected ? "ink.1000" : "border.light"}
                color={selected ? "text.dark" : "text.light"}
                fontFamily="body"
                fontSize="2xs"
                lineHeight="3xs"
                fontWeight="semiBold"
                disabled={disabled}
                onClick={() =>
                  setValue("availabilityType", type, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              >
                {t(key)}
              </styled.button>
            );
          })}
        </Flex>

        {availabilityType === EnumPromotionAvailabilityType.SpecificDates && (
          <>
            <Text visual="bodyRegular" color="text.light">
              {t("ADD_ONE_OR_MORE")}
            </Text>
            <Flex flexDir="column" gap="150">
              <Flex flexDir="column" gap="100">
                {dates.map((date, index) => (
                  <Flex key={`${index}`} gap="100" alignItems="center">
                    <PromotionDateField
                      value={date}
                      placeholder={t("DATE_PLACEHOLDER")}
                      nonSelectableDates={dates}
                      locale={locale}
                      disabled={disabled}
                      reduceZIndex={openIndex !== null && openIndex !== index}
                      onOpenChange={open => handleOpenChange(index, open)}
                      onValueChange={value => {
                        const next = [...dates];
                        next[index] = value;
                        setDates(next);
                      }}
                    />
                    <Button
                      visual="primary"
                      mode="light"
                      type="button"
                      w="40px"
                      h="40px"
                      p="0"
                      flexShrink={0}
                      borderRadius="100"
                      aria-label={t("REMOVE_DATE")}
                      disabled={disabled || dates.length <= 1}
                      onClick={() =>
                        setDates(dates.filter((_, i) => i !== index))
                      }
                    >
                      <Icon Element={Xmark} width="20px" height="20px" />
                    </Button>
                  </Flex>
                ))}
              </Flex>
              {datesError && (
                <Text visual="smallMedium" color="text.error.mid">
                  {datesError}
                </Text>
              )}
              <Button
                visual="unstyled"
                type="button"
                width="fit-content"
                p="0"
                color={{
                  base: "text.accent.blue.mid",
                  hover: "text.accent.blue.dark",
                  focusVisible: "text.accent.blue.dark",
                }}
                fontSize="md"
                lineHeight="sm"
                fontWeight="medium"
                disabled={disabled || dates.some(date => !date)}
                onClick={() => setDates([...dates, ""])}
              >
                {t("ADD_DATE")}
              </Button>
            </Flex>
          </>
        )}

        {availabilityType === EnumPromotionAvailabilityType.DateRange && (
          <>
            <Text visual="bodyRegular" color="text.light">
              {t("RANGE_HINT")}
            </Text>
            <Flex gap="100">
              <Flex flexDir="column" gap="50" flex="1">
                <Text visual="smallMedium" color="text.light">
                  {t("START_DATE")}
                </Text>
                <PromotionDateField
                  value={startDate}
                  placeholder={t("DATE_PLACEHOLDER")}
                  locale={locale}
                  disabled={disabled}
                  reduceZIndex={openIndex === 1}
                  onOpenChange={open => handleOpenChange(0, open)}
                  onValueChange={value => setRangeDate("startDate", value)}
                />
              </Flex>
              <Flex flexDir="column" gap="50" flex="1">
                <Text visual="smallMedium" color="text.light">
                  {t("END_DATE")}
                </Text>
                <PromotionDateField
                  value={endDate}
                  placeholder={t("DATE_PLACEHOLDER")}
                  min={startDate || undefined}
                  locale={locale}
                  disabled={disabled}
                  reduceZIndex={openIndex === 0}
                  onOpenChange={open => handleOpenChange(1, open)}
                  onValueChange={value => setRangeDate("endDate", value)}
                />
              </Flex>
            </Flex>
            {rangeError && (
              <Text visual="smallMedium" color="text.error.mid">
                {rangeError}
              </Text>
            )}
          </>
        )}

        {availabilityType === EnumPromotionAvailabilityType.Ongoing && (
          <Text visual="bodyRegular" color="text.light">
            {t("ONGOING_HINT")}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};
